import { useEffect } from "react";
import { graphql } from "@repo/graphql-types";
import { Calendar, Check, LocationMarker, Map as MapIcon } from "@repo/icons";
import {
  diffInMinutes,
  formatIgnoringTimezone,
  getFormattedTimeFromNumbers,
  isConsultorioVirtual,
  TimeSlotSuggestionType,
} from "@repo/lib";
import { useRouteContext, useRouter, useSearch } from "@tanstack/react-router";
import { CpsAlert, CpsSpinner } from "corpus";
import { useAtom, useSetAtom } from "jotai";
import {
  AppointmentAvailableOptionsSection,
  type Slot,
} from "@/components/appointment-available-options-section";
import type { AvailableClosestLocationDayEvents } from "@/components/appointment-form-calendar";
import { ReportUnavailabilityDrawer } from "@/components/report-unavailability-drawer";
import { useGraphQL } from "@/hooks/use-graphql";
import {
  appointmentFormAtom,
  trackingAppointmentAtom,
} from "@/lib/atoms/appointment-form-atom";
import { formatTimeHourMinute, formatTimeHourMinuteByTotalMinutes } from "@/lib/time";
import { trackEvent } from "@/lib/tracking";
import { AsyncDataWrapper } from "@/components/async-data-wrapper";
import { SchedulingInOtherLocationDrawer } from "@/components/scheduling-in-other-location-drawer";

const AppointmentAvailableOptionsFormQuery = graphql(`
  query AppointmentAvailableOptionsFormQuery(
    $codUnidadeOrigem: Int!
    $codUsuarioCompromisso: Int!
    $data: Date!
    $horarioInicio: TimeSpan!
    $codUsuario: Int!
  ) {
    buscaHorariosEmUnidadesAlternativas(
      codUnidadeOrigem: $codUnidadeOrigem
      codUsuario: $codUsuario
      codUsuarioCompromisso: $codUsuarioCompromisso
      data: $data
      horarioInicio: $horarioInicio
      comHorariosMenores: true
    ) {
      unidadesProximas {
        horarios {
          codUnidade
          dataHoraFim
          dataHoraInicio
          distancia
          nomeLimpoUnidade
          siglaUnidade
          tipoHorarioSugestao
        }
        totalUnidadesBuscadas
      }
      unidadesComAgendaAberta {
        horarios {
          codUnidade
          dataHoraFim
          dataHoraInicio
          distancia
          nomeLimpoUnidade
          siglaUnidade
          tipoHorarioSugestao
        }
        totalUnidadesBuscadas
      }
    }
  }
`);

export const AppointmentAvailableOptionsForm = (): JSX.Element => {
  const [atomValues, setAtomValues] = useAtom(appointmentFormAtom);
  const setTrackingAtom = useSetAtom(trackingAppointmentAtom);
  const router = useRouter();
  const { user } = useRouteContext({ strict: false });

  const params = useSearch({
    from: "/appointment/create/available-options",
  });

  const {
    locationId,
    locationName,
    appointmentType,
    selectedDate,
    selectedTime,
    action,
  } = params;

  const shouldShowUnavailableShowReportDrawer = action === "unavailable-time-report";
  const shouldShowScheduleInOtherLocationDrawer = action === "schedule-in-other-location";

  const handleOpenDrawer = (open: boolean, actionToHandle: string): void => {
    if (!open) {
      router.history.back();
    } else {
      void router.navigate({
        search: {
          ...params,
          action: actionToHandle,
        },
      });
    }
  };

  const { appointmentTime, selectedLocationTimeSlots, unitAcronym, roomTypes } =
    atomValues;

  const queryResult = useGraphQL(
    AppointmentAvailableOptionsFormQuery,
    {
      codUnidadeOrigem: locationId,
      codUsuarioCompromisso: appointmentType,
      data: selectedDate,
      horarioInicio: selectedTime,
      codUsuario: user.codUsuario,
    },
    {
      enabled: !isConsultorioVirtual(locationId),
    },
  );

  const handleDefaultSubmit = (slot: Slot): void => {
    setTrackingAtom({
      appointmentTypeId: appointmentType,
      desiredLocationId: locationId,
      desiredDate: selectedDate,
      desiredTime: selectedTime,
      scheduledLocationId: slot.unit.id,
      scheduledDate: String(slot.start),
      scheduledTime: formatIgnoringTimezone(
        slot.start ?? new Date(new Date().setHours(17, 0, 0, 0)),
        "HH:mm",
      ),
      scheduledTimeSlotType: slot.timeSlotType ?? TimeSlotSuggestionType.Original,
    });

    setAtomValues({
      ...atomValues,
      unit: slot.unit.id,
      unitName: slot.unit.name,
      showAppointmentInfo: true,
      appointmentTime: {
        start: slot.start ?? new Date(new Date().setHours(17, 0, 0, 0)),
        end: slot.end ?? new Date(new Date().setHours(18, 0, 0, 0)),
      },
      appointmentDate: slot.start,
    });

    navigateToPatientSearch();
  };

  const navigateToPatientSearch = (): void => {
    const isInOtherLocationDrawer = action === "schedule-in-other-location";
    void router.navigate({
      to: "/appointment/create/search-patient",
      search: {
        from: "appointment-available-options",
      },
      replace: isInOtherLocationDrawer,
    });
  };

  const handleSubmitWithConfirmation = (slot: Slot): void => {
    setTrackingAtom({
      appointmentTypeId: appointmentType,
      desiredLocationId: locationId,
      desiredDate: selectedDate,
      desiredTime: selectedTime,
      scheduledLocationId: slot.unit.id,
      scheduledDate: String(
        formatIgnoringTimezone(slot.start ?? new Date(), "yyyy-MM-dd"),
      ),
      scheduledTime: formatIgnoringTimezone(
        slot.start ?? new Date(new Date().setHours(17, 0, 0, 0)),
        "HH:mm:ss",
      ),
      scheduledTimeSlotType: slot.timeSlotType ?? TimeSlotSuggestionType.Original,
    });

    setAtomValues({
      ...atomValues,
      unit: slot.unit.id,
      unitName: slot.unit.name,
      showAppointmentInfo: true,
      appointmentTime: {
        start: slot.start ?? new Date(new Date().setHours(17, 0, 0, 0)),
        end: slot.end ?? new Date(new Date().setHours(18, 0, 0, 0)),
      },
      appointmentDate: slot.start,
    });

    void router.navigate({
      search: {
        ...params,
        action: "schedule-in-other-location",
      },
    });
  };

  const availableNearLocationsTimeSlots =
    queryResult.data?.buscaHorariosEmUnidadesAlternativas?.unidadesProximas.horarios.map(
      (closestLocation, index) => {
        return {
          id: `available-id ${index + 1}`,
          title: "Disponível",
          type: "available",
          appointmentType: "",
          unit: {
            id: closestLocation.codUnidade,
            name: closestLocation.nomeLimpoUnidade,
            acronym: closestLocation.siglaUnidade,
          },
          timeSlotType: closestLocation.tipoHorarioSugestao,
          start: new Date(closestLocation.dataHoraInicio),
          end: new Date(closestLocation.dataHoraFim),
          distance: closestLocation.distancia,
        } as AvailableClosestLocationDayEvents;
      },
    );

  const availableNearLocationsTimeSlotsCount =
    queryResult.data?.buscaHorariosEmUnidadesAlternativas?.unidadesProximas
      .totalUnidadesBuscadas;

  const availableOpenScheduleLocationsTimeSlots =
    queryResult.data?.buscaHorariosEmUnidadesAlternativas?.unidadesComAgendaAberta.horarios.map(
      (location, index) => {
        return {
          id: `available-id ${index + 1}`,
          title: "Disponível",
          type: "available",
          appointmentType: "",
          unit: {
            id: location.codUnidade,
            name: location.nomeLimpoUnidade,
            acronym: location.siglaUnidade,
          },
          timeSlotType: location.tipoHorarioSugestao,
          start: new Date(location.dataHoraInicio),
          end: new Date(location.dataHoraFim),
          distance: location.distancia,
        } as AvailableClosestLocationDayEvents;
      },
    );
  const availableOpenScheduleLocationsTimeSlotsCount =
    queryResult.data?.buscaHorariosEmUnidadesAlternativas?.unidadesComAgendaAberta
      .totalUnidadesBuscadas;

  const anySlotInSelectedLocation = (selectedLocationTimeSlots?.length ?? 0) > 0;
  const appointmentStartTime = appointmentTime.start;
  const appointmentEndTime = appointmentTime.end;
  const hourSelectedTimeFormatted = formatTimeHourMinute(
    getFormattedTimeFromNumbers(
      Number(appointmentStartTime.getHours()),
      appointmentStartTime.getMinutes(),
    ),
  );

  const reportUnavailability = (): void => {
    trackEvent("Agendamento Feedback Horario Coletado", {
      codUnidade: locationId,
      siglaUnidade: unitAcronym,
      codTiposSalas: roomTypes,
      duracaoTeorica: formatTimeHourMinuteByTotalMinutes(
        diffInMinutes(appointmentEndTime, appointmentStartTime),
      ),
      data: appointmentStartTime,
      horarioBuscado: hourSelectedTimeFormatted,
    });
  };

  useEffect(() => {
    if (queryResult.data) {
      const getSlotInfo = (
        slot: Slot,
      ): {
        distancia?: number;
        inicio?: Date;
        fim?: Date;
        tipoHorarioSugestao?: number;
        codUnidade: number;
      } => {
        return {
          distancia: slot.distance,
          inicio: slot.start,
          fim: slot.end,
          tipoHorarioSugestao: slot.timeSlotType,
          codUnidade: slot.unit.id,
        };
      };
      trackEvent("Horários Unidades Alternativas Buscados", {
        horariosDisponiveisUnidadesProximas:
          availableNearLocationsTimeSlots?.map(getSlotInfo) ?? [],
        totalUnidadesProximasBuscadas: availableNearLocationsTimeSlotsCount,
        horariosDisponiveisUnidadesComAgendaAberta:
          availableOpenScheduleLocationsTimeSlots?.map(getSlotInfo) ?? [],
        totalUnidadesProximasComAgendaAberta:
          availableOpenScheduleLocationsTimeSlotsCount,
        codUnidadeDesejada: locationId,
        horariosDisponiveisUnidadeDesejada:
          selectedLocationTimeSlots?.map(getSlotInfo) ?? [],
        horarioDesejado: selectedTime,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Apenas no carregamento inicial da página
  }, [queryResult.data]);

  const unavailableReportTitle =
    availableNearLocationsTimeSlots?.length === 0 &&
    availableOpenScheduleLocationsTimeSlots?.length === 0
      ? "Lamentamos mas no momento não há salas disponíveis no horário solicitado em nenhuma das opções que buscamos."
      : "Não há salas disponíveis no horário solicitado, mas encontramos opções próximas. Nenhuma sugestão de horário te atende?";

  return (
    <div className="flex flex-col py-4 gap-4">
      <div className="px-4">
        <p className="text-left text-xl text-neutral-600 font-medium w-full">
          Outras opções
        </p>
        <p className="text-left text-md text-neutral-500 w-full mt-1.5">
          O horário escolhido não está disponível na unidade selecionada, buscamos algumas
          opções alternativas para você.
        </p>
      </div>

      <AppointmentAvailableOptionsSection
        slots={selectedLocationTimeSlots ?? []}
        headerTitle="Unidade selecionada"
        icon={LocationMarker}
        extraLabel="Unidade selecionada"
        extraLabelIcon={Check}
        onClickOption={handleDefaultSubmit}
        selectedUnitName={locationName}
      />

      {!isConsultorioVirtual(locationId) ? (
        <AsyncDataWrapper
          {...queryResult}
          fallback={
            <div className="my-6 flex gap-4 justify-center items-center flex-col">
              <CpsSpinner />
              <h3 className="text-neutral-500 font-semibold">
                Procurando em outras unidades...
              </h3>
            </div>
          }
        >
          <AppointmentAvailableOptionsSection
            slots={availableNearLocationsTimeSlots ?? []}
            headerTitle="Unidades próximas"
            icon={MapIcon}
            extraLabel="Alternativa mais próxima"
            selectedUnitName={locationName}
            onClickOption={handleSubmitWithConfirmation}
            countLocationsSearchResults={availableNearLocationsTimeSlotsCount}
          />

          <AppointmentAvailableOptionsSection
            slots={availableOpenScheduleLocationsTimeSlots ?? []}
            headerTitle="Unidades com agenda aberta"
            icon={Calendar}
            selectedUnitName={locationName}
            onClickOption={handleSubmitWithConfirmation}
            countLocationsSearchResults={availableOpenScheduleLocationsTimeSlotsCount}
          />

          <SchedulingInOtherLocationDrawer
            open={shouldShowScheduleInOtherLocationDrawer}
            setOpen={() => {
              handleOpenDrawer(false, "schedule-in-other-location");
            }}
            submitAction={navigateToPatientSearch}
          />
        </AsyncDataWrapper>
      ) : null}

      {!anySlotInSelectedLocation ? (
        <div className="mt-4 px-4 pb-2">
          <CpsAlert
            title={unavailableReportTitle}
            type="info"
            linkTitle="Reportar indisponibilidade"
            linkPosition="bottom"
            linkProps={{
              onClick: () => {
                handleOpenDrawer(true, "unavailable-time-report");
              },
              className: "cursor-pointer",
            }}
          />
          <ReportUnavailabilityDrawer
            open={shouldShowUnavailableShowReportDrawer}
            setOpen={() => {
              handleOpenDrawer(false, "unavailable-time-report");
            }}
            reportUnavailabilityTrackEvent={reportUnavailability}
            desiredTime={hourSelectedTimeFormatted}
          />
        </div>
      ) : null}
    </div>
  );
};
